import { observer } from 'mobx-react-lite';

import {
  CartSiteAddress,
  useCartContext,
} from 'screens/AccessExpressV3/CartProvider';
import { ProductBody } from '../../productFlow';
import Locations from '../../Locations/Locations';
import clsx from 'clsx';
import { useAuthUser } from 'hooks/useAuthUser';
import { postQuote, updateOrder } from 'api/accessexpressv3/api';
import { postQuoteToRequest } from 'screens/AccessExpressV3/AccessProductDetails/utils';
import { useEffect, useRef, useState } from 'react';
import { SearchAccessAddressBar } from '../../Locations/SearchAccessAddressBar';
import { QuoteLocation } from 'api/accessexpress/schema';
import axios from 'axios';
import { toJS } from 'mobx';

let cancelTokenSource = axios.CancelToken.source();

const LocationBody = observer(() => {
  const cart = useCartContext();
  const userInfo = useAuthUser();

  const handleLocationsChange = async (locations: Array<CartSiteAddress>) => {
    if (cancelTokenSource) {
      cancelTokenSource?.cancel('Operation canceled due to new request.');
    }
    // Create a new controller for the current request
    cancelTokenSource = axios.CancelToken.source();
    // 1. set new or order locations and update the order locations on BE
    if (!cart.isEditingLocations) cart.setLoadingDraftQuote(true);
    cart.setLocations(locations);
    if (cart.draftId && !cart.isEditingLocations) {
      // NOTE: This logic is duplicated across multiple components (handleFindServices, syncCartWithBackend).
      const dataForPostOrUpdate = {
        additional_details: cart.additionalDetails ?? {},
        locations: cart.locations.map((location) => {
          const order_items = cart
            .itemsByLocation(location.id)
            .filter((item) => item.category === 'Access')
            .map((item) => ({
              type: item.category ?? '',
              subtype: item.subCategory ?? '',
              // need to handle
              pim_price_id: null,
              item_details: toJS(item.item_details) ?? [],
              price: item.price ?? 0,
              quantity: item.quantity ?? 0,
              location_id: String(item.locationId ?? ''),
            }));
          return {
            address: {
              ...location,
              id: location.id as string,
            },
            order_items,
          };
        }),
      };
      const draft = await updateOrder(dataForPostOrUpdate, cart.draftId, {
        cancelToken: cancelTokenSource.token,
      });
      // 2. await endpoint to return order update locations
      // 3. store order locations on state;
      cart.updateStateFromDraft(draft);
      // 4. create quote
      await postQuote(
        postQuoteToRequest(
          userInfo,
          cart.locations,
          [
            {
              product_type: cart.selectedAccessProductCategory as
                | 'Broadband'
                | 'DIA',
              ...cart.additionalDetails?.configs,
            },
          ],
          // draft.locations?.map((l) => ({
          //   address: { address_line_1: l.address?.address_line_1 ?? '' },
          //   id: l?.id ?? '',
          // })) ?? [],
        ),
        { cancelToken: cancelTokenSource.token },
      )
        .then(async (quoteResponse) => {
          const additionalDetails = {
            configs: cart.additionalDetails?.configs,
            ...(cart.selectedAccessProductCategory === 'DIA'
              ? { dia_quote_request: quoteResponse.id }
              : { bb_quote_request: quoteResponse.id }),
          };
          await updateOrder(
            {
              additional_details: additionalDetails,
            },
            draft?.id ?? '',
            { cancelToken: cancelTokenSource.token },
          )
            .then((data) => cart.updateStateFromDraft(data))
            .finally(() => {
              cart.setLoadingDraftQuote(false);
            });
        })
        .catch((error) => {
          if (axios?.isAxiosError(error) && error?.name === 'CanceledError')
            return;
          cart.setLoadingDraftQuote(false);
          console.log('Error creating quote:', error);
        });
      // 5. the request will return quote_id
      // 6. update draft (BE + state) with quote_id
    }
  };
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setCurrentIndex(() => {
      const _currentIndex = cart.locations.findIndex(
        (l) => l.id === cart.locationActiveId,
      );
      return _currentIndex;
    });
  }, [cart.locationActiveId, cart.locations]);

  const locationRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    locationRefs.current = locationRefs.current.slice(0, cart.locations.length);
  }, [cart.locations]);

  const [totalHeightAbove, setTotalHeightAbove] = useState(0);

  useEffect(() => {
    setTotalHeightAbove(() => {
      const _totalHeightAbove =
        locationRefs.current
          .slice(0, currentIndex)
          .reduce((sum, ref) => sum + (ref?.offsetHeight || 0), 0) +
        currentIndex * 32;
      return _totalHeightAbove;
    });
  }, [currentIndex]);

  if (cart.isEditingLocations) {
    return (
      <Locations locations={cart.locations} onChange={handleLocationsChange} />
    );
  }

  return (
    <div className={clsx('')}>
      <div
        className={clsx(
          'relative flex h-full flex-col gap-8',
          'duration-[600ms] ease-in-out',
        )}
        style={{ top: `-${totalHeightAbove}px` }}
      >
        {cart.locations.map((location, index) => (
          <div
            key={location.id}
            ref={(el) => (locationRefs.current[index] = el)}
            className={clsx(
              'relative flex min-h-[calc(100vh-505px)] flex-col gap-2 xl:min-h-[calc(100vh-445px)]',
              {
                'pointer-events-none opacity-40':
                  cart.locationActiveId !== location.id,
                'mb-16': cart.totalItems === 0 && index === currentIndex,
              },
            )}
          >
            <SearchAccessAddressBar
              onAddressSelected={() => {}}
              className="flex [&_.search-bar-label]:w-[302px] [&_.search-bar-label]:py-0.5 [&_.search-bar-label]:text-sm [&_button]:hidden"
              value={location as QuoteLocation}
              prefixElement={
                <div className="-ml-2 flex w-4 flex-none justify-end text-sm text-content-base-subdued">
                  {index + 1}.
                </div>
              }
            />

            {cart.itemsByLocation(location.id).length > 0 ? (
              <>
                <ProductBody locationId={location.id} category="Access" />
              </>
            ) : (
              <>
                <div className="rounded border border-dashed border-stroke-base-default p-2 text-center text-xs font-bold text-button-content-primary-disabled">
                  No services added
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
});

export default LocationBody;
